import { NotificationsType } from 'src/api';
import {
  getCaseReadableId,
  getReadableIdWithDash,
  NavbarVariantEnum,
} from 'src/types';
import { sanitationInfoIntercomArticle } from 'src/utils/intercomArticles';
import I18next from '../../../i18n';

export const reportIdNotificationTypes = [
  NotificationsType.MESSAGE,
  NotificationsType.APP_MESSAGE,
  NotificationsType.APP_STATUS_CHANGE,
  NotificationsType.REPORTER_HUB_MESSAGE,
  NotificationsType.DATA_SANITISATION,
  NotificationsType.DATA_SANITISATION_REPORTER_ANONYMISED,
  NotificationsType.REPORT_SUBMITTED,
  NotificationsType.OPR_STATUS_CHANGE,
  NotificationsType.OPR_MESSAGE,
  NotificationsType.APP_REPORT_REASSIGNED,
  NotificationsType.APP_MATCH,
  NotificationsType.APP_FILE_SCAN_FAILED,
];

const importNotificationTypes = [
  NotificationsType.IMPORT_JOB_COMPLETED_SUCCESSFULLY,
  NotificationsType.IMPORT_JOB_PARTIALLY_SUCCEEDED,
  NotificationsType.IMPORT_JOB_FAILED,
];

export const getNotificationTitle = (notificationType: string) => {
  switch (notificationType) {
    case NotificationsType.ASSIGNED:
    case NotificationsType.CASE_REASSIGNED:
      return I18next.t('general.navbar.notificationsDrawer.caseAssigned');
    case NotificationsType.REASSIGNED:
      return I18next.t('general.navbar.notificationsDrawer.reportAssigned');
    case NotificationsType.NEW:
      return I18next.t('general.navbar.notificationsDrawer.newReport');
    case NotificationsType.STALE:
      return I18next.t('general.navbar.notificationsDrawer.staleCase');
    case NotificationsType.MESSAGE:
      return I18next.t('general.navbar.notificationsDrawer.newMessage');
    case NotificationsType.CASE_TEAM_INVITE:
      return I18next.t('general.navbar.notificationsDrawer.caseTeamInvite');
    case NotificationsType.PERMISSIONS_REMOVED:
      return I18next.t('general.navbar.notificationsDrawer.permissionRemoved');
    case NotificationsType.POST_SUBMISSION_FILES_ADDED:
      return I18next.t(
        'general.navbar.notificationsDrawer.postSSubmissionFile',
      );
    case NotificationsType.APP_MESSAGE:
    case NotificationsType.OPR_MESSAGE:
    case NotificationsType.REPORTER_HUB_MESSAGE:
      return I18next.t('general.navbar.notificationsDrawer.newMessage');
    case NotificationsType.APP_STATUS_CHANGE:
    case NotificationsType.OPR_STATUS_CHANGE:
      return I18next.t('general.navbar.notificationsDrawer.reportStatusChange');
    case NotificationsType.DATA_SANITISATION_REPORTER_ANONYMISED:
      return I18next.t('general.navbar.notificationsDrawer.reporterAnonymised');
    case NotificationsType.DATA_SANITISATION:
      return I18next.t('general.navbar.notificationsDrawer.dataSanitisation');
    case NotificationsType.REPORT_SUBMITTED:
      return I18next.t(
        'general.navbar.notificationsDrawer.reporterReportSubmitted.title',
      );
    case NotificationsType.APP_REPORT_REASSIGNED:
      return I18next.t(
        'general.navbar.notificationsDrawer.appReportReassigned',
      );
    case NotificationsType.APP_MATCH:
      return I18next.t('general.navbar.notificationsDrawer.appReportMatch');
    case NotificationsType.APP_FILE_SCAN_FAILED:
      return I18next.t('general.navbar.notificationsDrawer.appFileScanFailed');
    case NotificationsType.CASE_LINK_CREATED:
      return I18next.t('general.navbar.notificationsDrawer.caseLinked');
    case NotificationsType.CASE_LINK_DELETED:
      return I18next.t('general.navbar.notificationsDrawer.caseUnlinked');
    case NotificationsType.IMPORT_JOB_COMPLETED_SUCCESSFULLY:
      return I18next.t(
        'general.navbar.notificationsDrawer.caseImporterJobCompleted.success',
      );
    case NotificationsType.IMPORT_JOB_PARTIALLY_SUCCEEDED:
      return I18next.t(
        'general.navbar.notificationsDrawer.caseImporterJobCompleted.PartialSuccess',
      );
    case NotificationsType.IMPORT_JOB_FAILED:
      return I18next.t(
        'general.navbar.notificationsDrawer.caseImporterJobCompleted.failure',
      );
    case NotificationsType.MITIGATION_PLAN_REMINDER:
      return I18next.t(
        'general.navbar.notificationsDrawer.mitigationPlanReminder',
      );
    default:
      return 'Notification';
  }
};

export const getNotificationSecondaryText = (
  notificationType: string,
  caseOrReportId: string | null | undefined,
  details: string | null | undefined,
) => {
  if (
    reportIdNotificationTypes.includes(notificationType as NotificationsType)
  ) {
    return `${I18next.t(
      'general.navbar.notificationsDrawer.report',
    )} ${getReadableIdWithDash(caseOrReportId)}`;
  }
  if (importNotificationTypes.includes(notificationType as NotificationsType)) {
    return `${I18next.t('general.navbar.notificationsDrawer.importId')} ${
      JSON.parse(details || '{}').importId
    }`;
  }
  return `${I18next.t(
    'general.navbar.notificationsDrawer.case',
  )} ${getCaseReadableId(caseOrReportId)}`;
};

export const getNotificationImageSrc = (notificationType: string) => {
  switch (notificationType) {
    case NotificationsType.STALE:
      return '/assets/icon_unactioned_report.png';
    case NotificationsType.MESSAGE:
      return '/assets/icon_message_green.png';
    default:
      return '/assets/icon_report_green.png';
  }
};

export const getNotificationIcon = (notificationType: string) => {
  const messageIconNotificationTypes = [
    NotificationsType.POST_SUBMISSION_FILES_ADDED,
    NotificationsType.CASE_TEAM_INVITE,
  ];
  if (notificationType === NotificationsType.DATA_SANITISATION) {
    return 'uil-sync';
  }
  if (importNotificationTypes.includes(notificationType as NotificationsType)) {
    return 'uil-file-import';
  }
  return messageIconNotificationTypes.includes(
    notificationType as NotificationsType,
  )
    ? 'uil-comment-alt-message'
    : 'uil-file-alt';
};

export const getNotificationHelpLinkUrl = (notificationType: string) => {
  const helpLinkNotificationTypes = [
    NotificationsType.DATA_SANITISATION,
    NotificationsType.REPORT_SUBMITTED,
  ];
  if (
    helpLinkNotificationTypes.includes(notificationType as NotificationsType)
  ) {
    return sanitationInfoIntercomArticle;
  }
  return undefined;
};

export const getNotificationSubtitle = (notificationType: string) => {
  switch (notificationType) {
    case NotificationsType.REPORT_SUBMITTED:
      return I18next.t(
        'general.navbar.notificationsDrawer.reporterReportSubmitted.subtitle',
      );
    default:
      return undefined;
  }
};

export const getNotificationItemUrl = ({
  variant,
  caseOrReportId,
  organisation,
  isOPRMessageNotification,
  type,
  importType,
}: {
  variant: NavbarVariantEnum;
  caseOrReportId: string | null | undefined;
  organisation?: string;
  isOPRMessageNotification?: boolean;
  type?: string;
  importType?: string | null;
}) => {
  if (importNotificationTypes.includes(type as NotificationsType)) {
    return `/uploadCasesHistory`;
  }

  if (
    type === NotificationsType.MITIGATION_PLAN_REMINDER ||
    importType === 'coi'
  ) {
    return `/disclosure?id=${caseOrReportId}`;
  }

  switch (variant) {
    case NavbarVariantEnum.REPORTER:
      return `/reporting/reports/${caseOrReportId}`;
    case NavbarVariantEnum.PUBLIC:
      return isOPRMessageNotification
        ? `/${organisation}/open-reporting/messages/${caseOrReportId}`
        : `/${organisation}/open-reporting/reports/${caseOrReportId}`;
    default:
      return `/case?id=${caseOrReportId}`;
  }
};
